import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail as firebaseSendPasswordResetEmail,
    updatePassword as firebaseUpdatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, COLLECTIONS } from '../config/firebase';
import { generateReferralCode } from '../utils/helpers';

// User Data Type
export const createUserData = (uid, phoneNumber, referralCode, referredBy = null) => ({
    uid,
    phoneNumber,
    email: null,
    displayName: phoneNumber,
    photoURL: null,
    referralCode: referralCode ? referralCode.toUpperCase() : '',
    referredBy: referredBy ? referredBy.toUpperCase() : null,
    rechargeBalance: 0,
    withdrawBalance: 0,
    fundPassword: null,
    createdAt: new Date(),
    updatedAt: new Date(),
});

// Sign Up with Phone and Password
export const signUpWithPhone = async (phoneNumber, password, referredByCode = null) => {
    try {
        // Check if phone number is already registered and blocked
        const internalEmail = `${phoneNumber}@nextron.com`;
        try {
            const userCredential = await signInWithEmailAndPassword(auth, internalEmail, 'test');
        } catch (e) {
            // If user exists, check their status
            if (e.code === 'auth/wrong-password') {
                const { collection, query, where, getDocs } = await import('firebase/firestore');
                const usersRef = collection(db, COLLECTIONS.USERS);
                const q = query(usersRef, where('phoneNumber', '==', phoneNumber));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    const userData = snapshot.docs[0].data();
                    if (userData.status === 'blocked' || userData.status === 'suspended') {
                        throw new Error('This phone number is blocked. Please contact support.');
                    }
                    throw new Error('Phone number already registered. Please login instead.');
                }
            }
        }
        
        // Validate referral code if provided
        if (referredByCode && referredByCode.trim() !== '') {
            const isValid = await validateReferralCode(referredByCode);
            if (!isValid) {
                throw new Error('Invalid referral code');
            }
        }

        // Map phone to internal email for Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, internalEmail, password);
        const user = userCredential.user;

        // Find referrer UID
        let referredByUid = null;
        if (referredByCode && referredByCode.trim() !== '') {
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const q = query(collection(db, COLLECTIONS.USERS), where('referralCode', '==', referredByCode.trim().toUpperCase()));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                referredByUid = snapshot.docs[0].id;
            }
        }

        // Generate unique referral code
        const referralCode = generateReferralCode();

        // Create user document
        const userData = createUserData(
            user.uid,
            phoneNumber,
            referralCode,
            referredByCode?.trim()?.toUpperCase() || null
        );

        await setDoc(doc(db, COLLECTIONS.USERS, user.uid), {
            ...userData,
            referredByUid,
            status: 'active',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        return user;
    } catch (error) {
        console.error('Sign up error:', error);
        throw error;
    }
};

// Sign In with Phone and Password
export const signInWithPhone = async (phoneNumber, password) => {
    try {
        const internalEmail = `${phoneNumber}@nextron.com`;
        const userCredential = await signInWithEmailAndPassword(auth, internalEmail, password);
        
        // Check if user is blocked
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userCredential.user.uid));
        if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.status === 'blocked' || userData.status === 'suspended') {
                // Sign out the user immediately
                await signOut(auth);
                throw new Error('Your account has been blocked. Please contact support.');
            }
        }
        
        return userCredential.user;
    } catch (error) {
        console.error('Sign in error:', error);
        throw error;
    }
};

// Sign Out
export const logout = async () => {
    try {
        await signOut(auth);
    } catch (error) {
        console.error('Sign out error:', error);
        throw error;
    }
};

// Validate Referral Code
export const validateReferralCode = async (code) => {
    if (!code || code.trim() === '') return true;

    try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const usersRef = collection(db, COLLECTIONS.USERS);
        const q = query(usersRef, where('referralCode', '==', code.toUpperCase()));
        const snapshot = await getDocs(q);
        return !snapshot.empty;
    } catch (error) {
        console.error('Error validating referral code:', error);
        return false;
    }
};

// Send Password Reset Email
export const sendPasswordResetEmail = async (email) => {
    try {
        await firebaseSendPasswordResetEmail(auth, email);
    } catch (error) {
        console.error('Password reset error:', error);
        throw error;
    }
};

// Update Password
export const updatePassword = async (newPassword) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');
        await firebaseUpdatePassword(user, newPassword);
    } catch (error) {
        console.error('Update password error:', error);
        throw error;
    }
};

// Set Fund Password
export const setFundPassword = async (fundPassword) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        await updateDoc(doc(db, COLLECTIONS.USERS, user.uid), {
            fundPassword,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Set fund password error:', error);
        throw error;
    }
};

// Get User Data
export const getUserData = async (uid) => {
    try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid));
        if (userDoc.exists()) {
            return { uid: userDoc.id, ...userDoc.data() };
        }
        return null;
    } catch (error) {
        console.error('Get user data error:', error);
        throw error;
    }
};

// Update User Profile
export const updateUserProfile = async (updates) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        await updateDoc(doc(db, COLLECTIONS.USERS, user.uid), {
            ...updates,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Update profile error:', error);
        throw error;
    }
};

export default {
    signUpWithPhone,
    signInWithPhone,
    logout,
    validateReferralCode,
    sendPasswordResetEmail,
    updatePassword,
    setFundPassword,
    getUserData,
    updateUserProfile,
};
